/**
 * Layout compartilhado pelas páginas legais (Termos, Privacidade, Excluir conta).
 *
 * Migrado do antigo `meiuca-site` (Next.js) — agora vive dentro do `meiuca-app-web-react`
 * com React Router. Header conta com logo + botão de voltar pra home. Conteúdo principal
 * vem como `children` em um container com largura máxima de leitura (`max-w-3xl`).
 * Footer minimalista com links cruzados entre os documentos legais.
 *
 * `relatedLinks` permite trocar a navegação cruzada do rodapé entre famílias de
 * documentos (consumidor, arenas, centros de treinamento). Quando omitido, usa a
 * navegação do app de consumidor.
 *
 * Define `document.title` no mount — não há `<Metadata>` do Next.js neste app.
 */

import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { AsaasSeal, Body, Caption, H1 } from '@meiuca/ui-components'

export interface RelatedLink {
  href: string
  label: string
}

interface LegalPageLayoutProps {
  title: string
  documentTitle?: string
  lastUpdated: string
  children: React.ReactNode
  relatedLinks?: RelatedLink[]
}

const defaultRelatedLinks: RelatedLink[] = [
  { href: '/termos', label: 'Termos de Uso' },
  { href: '/privacidade', label: 'Política de Privacidade' },
  { href: '/excluir-conta', label: 'Excluir conta' }
]

export function LegalPageLayout({
  title,
  documentTitle,
  lastUpdated,
  children,
  relatedLinks = defaultRelatedLinks
}: LegalPageLayoutProps) {
  useEffect(() => {
    if (documentTitle) {
      document.title = documentTitle
    }
  }, [documentTitle])

  return (
    <main className="min-h-screen bg-background-primary">
      <header className="border-b border-gray-200/40 bg-background-white/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link
              to="/"
              className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors"
            >
              <ArrowLeft size={18} />
              <span className="text-sm md:text-base">Voltar</span>
            </Link>

            <Link to="/" className="flex items-center gap-2">
              <img src="/favicon.svg" alt="Meiuca" className="w-7 h-7" width={28} height={28} />
              <span className="text-lg md:text-xl font-bold text-text-primary">Meiuca</span>
            </Link>

            <div className="w-16" aria-hidden="true" />
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <H1 className="mb-3 text-3xl md:text-4xl">{title}</H1>
        <Caption className="text-text-tertiary mb-10 block">
          Última atualização: {lastUpdated}
        </Caption>

        <div className="legal-content space-y-6">
          {children}
        </div>
      </article>

      <footer className="border-t border-gray-200/40 bg-background-secondary py-10 px-5 mt-16">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-8 flex flex-col items-center gap-2">
            <AsaasSeal size="md" />
            <Caption className="text-text-tertiary max-w-md">
              Serviços financeiros prestados pelo ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.,
              instituição autorizada pelo Banco Central do Brasil.
            </Caption>
          </div>

          <Body className="text-text-secondary mb-4">
            Outros documentos
          </Body>
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-text-tertiary">
            {relatedLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="hover:text-brand-primary transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </footer>
    </main>
  )
}
